import React, { FC, useEffect } from "react";
import { Description, Icon } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { ServiceInfoDc } from "@evergis/api";

import { RestrictedWidthContainer } from "../../../../DialogComponents/styled";
import { LinearProgress, LoadingDescription } from "../../../../BulkCopy/styled";
import { ResourceMeta } from "../../../../DialogComponents/ResourceMeta/ResourceMeta";

import {
  CopyTaskMessage,
  CopyTaskMessageContainer,
  ResultPreviewContainer,
  ResultPreviewObjectCounter,
  ResultPreviewObjectCounterTitle,
  ResultPreviewWrapper,
  TransferIconWrapper,
} from "../styled";

import { pipelineTask } from "../../../../../../resources/scheduler";
import { useProgress } from "../../../../../../hooks/scheduler/useProgress";
import { useLayerDetails } from "../hooks/useLayerDetails";
import { createPipelineTask } from "../../../../../../evergis/core/tools/createPipelineTask";
import { formatNumber } from "../../../../../../utils/formatNumber";
import { useRowsCount } from "../hooks/useRowsCount";
import { featuresCount, features } from "../../../../../../resources/features";
import { useLayerConditionFromTable } from "../hooks/useLayerConditionFromTable";
import { useDefaultScaleRange } from "../../../../../../hooks/style/useDefaultScaleRange";
import { layerWithTable } from "../../../../../../resources/layerWithTable";
import { layer } from "../../../../../../resources/layers";

import { Step2Props } from "../types";
import { LayerInfo } from "../../../../../../interfaces/resourceInfo";

// eslint-disable-next-line max-lines-per-function
export const Step2: FC<Step2Props> = ({ layerInfo, targetLayer, copyFields, onTaskStatusChange }) => {
  const { t } = useTranslation();
  const { meta: sourceMeta } = useLayerDetails(layerInfo as LayerInfo);
  const { meta: targetMeta } = useLayerDetails(targetLayer as LayerInfo);
  const [pipeline, { data }] = pipelineTask.use();
  const { isTaskExecuting, isTaskCompleted, isTaskFailed, result } = useProgress(data?.taskId);
  const rowCount = useRowsCount(layerInfo);
  const layerConditionFromTable = useLayerConditionFromTable(layerInfo as LayerInfo);
  const defaultScaleRange = useDefaultScaleRange();

  useEffect(() => {
    if (!layerInfo || !targetLayer || !copyFields.length) {
      return;
    }

    pipeline({
      input: createPipelineTask([
        {
          type: "copy",
          source: {
            type: "layerTaskDataStorage",
            serviceName: layerInfo.name,
          },
          target: {
            type: "layerTaskDataStorage",
            serviceName: targetLayer.name,
          },
          attributeMapping: copyFields.reduce(
            (acc, { sourceField, targetField }) => ({ ...acc, [sourceField]: targetField }),
            {},
          ),
          condition: layerConditionFromTable ?? undefined,
        },
      ]),
    }).then(() => {
      layer.invalidate(targetLayer.name);
      layerWithTable.invalidate({ name: targetLayer.name, defaultScaleRange });
      features.invalidateAll();
      featuresCount.invalidateAll();
    });
  }, [copyFields, layerInfo, targetLayer, pipeline, layerConditionFromTable, defaultScaleRange]);

  useEffect(() => {
    onTaskStatusChange(isTaskExecuting);
  }, [isTaskExecuting, onTaskStatusChange]);

  return (
    <RestrictedWidthContainer>
      <ResultPreviewWrapper>
        <ResourceMeta meta={sourceMeta} readOnly noMeta imageStyle={{ width: "10rem", height: "5.625rem" }}>
          <ResultPreviewContainer>
            <div>
              <div>{sourceMeta.alias}</div>
              {sourceMeta.description && <Description>{sourceMeta.description}</Description>}
            </div>
            <ResultPreviewObjectCounter>
              <ResultPreviewObjectCounterTitle>
                {t("copyResource.featuresToCopy", { ns: "dialogs" })}
              </ResultPreviewObjectCounterTitle>
              <div>{formatNumber(rowCount)}</div>
            </ResultPreviewObjectCounter>
          </ResultPreviewContainer>
        </ResourceMeta>
        <TransferIconWrapper style={{ width: "10rem" }}>
          <Icon kind="arrow_back" />
        </TransferIconWrapper>
        <ResourceMeta meta={targetMeta} readOnly noMeta imageStyle={{ width: "10rem", height: "5.625rem" }}>
          <ResultPreviewContainer>
            <div>
              <div>{targetMeta.alias}</div>
              {targetMeta.description && <Description>{targetMeta.description}</Description>}
            </div>
            <ResultPreviewObjectCounter>
              <ResultPreviewObjectCounterTitle>
                {t("copyResource.totalFeatures", { ns: "dialogs" })}
              </ResultPreviewObjectCounterTitle>
              <div>{formatNumber((targetLayer as ServiceInfoDc)?.objectCount)}</div>
            </ResultPreviewObjectCounter>
          </ResultPreviewContainer>
        </ResourceMeta>
      </ResultPreviewWrapper>
      {isTaskExecuting && (
        <>
          <LoadingDescription>{t("copyResource.copyingFeatures", { ns: "dialogs" })}...</LoadingDescription>
          <LinearProgress />
        </>
      )}
      {isTaskCompleted && !isTaskFailed && (
        <CopyTaskMessageContainer success>
          <Icon kind="success" />
          <div>
            {`${t("actions.ready", { ns: "common" })}!`}
            <CopyTaskMessage success>
              {result.outputObjectCount} {t("copyResource.of", { ns: "dialogs" })} {result.inputObjectCount}{" "}
              {t("copyResource.xObjectsCopiedToLayer", { ns: "dialogs" })} {targetLayer.alias || targetLayer.name}
            </CopyTaskMessage>
          </div>
        </CopyTaskMessageContainer>
      )}
      {isTaskCompleted && isTaskFailed && (
        <CopyTaskMessageContainer error>
          <Icon kind="error" />
          <div>
            {t("copyResource.error", { ns: "dialogs" })}
            <CopyTaskMessage error>{result.message}</CopyTaskMessage>
          </div>
        </CopyTaskMessageContainer>
      )}
    </RestrictedWidthContainer>
  );
};
